import React, { useState, useEffect } from "react";
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import indiaGeoJson from "../india.json";
import {
  FaUsers, FaUserGraduate, FaChalkboardTeacher, FaBriefcase,
  FaFileAlt, FaUserTie, FaPlusCircle, FaUserPlus, FaGift,
  FaUserSlash, FaNewspaper, FaNetworkWired, FaLaptop,
  FaCalendarAlt, FaHandshake, FaBuilding,
} from "react-icons/fa";
import ApiService from "../nhrc_apis/ApiService";

/* =========================================================
   HELPERS
========================================================= */
const safeArr = (res) => {
  if (!res || res.status !== "fulfilled") return [];
  const d = res.value?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.members)) return d.members;
  if (Array.isArray(d?.employees)) return d.employees;
  if (Array.isArray(d?.students)) return d.students;
  if (Array.isArray(d?.representatives)) return d.representatives;
  if (Array.isArray(d?.jobs)) return d.jobs;
  if (Array.isArray(d?.events)) return d.events;
  if (Array.isArray(d?.trainings)) return d.trainings;
  if (Array.isArray(d?.job_fairs)) return d.job_fairs;
  if (Array.isArray(d?.board_members)) return d.board_members;
  if (Array.isArray(d?.contacts)) return d.contacts;
  if (Array.isArray(d?.black_profiles)) return d.black_profiles;
  if (Array.isArray(d?.publications)) return d.publications;
  if (Array.isArray(d?.registrations)) return d.registrations;
  return [];
};

const normalizeStatus = (item) =>
  (item?.status || item?.membership_status || item?.approval_status ||
   item?.job_status || item?.event_status || "").toLowerCase().trim();

const countByStatus = (arr) => {
  const approved = arr.filter(i => { const s = normalizeStatus(i); return s === "approved" || s === "active" || s === "open"; }).length;
  const pending  = arr.filter(i => normalizeStatus(i) === "pending").length;
  const rejected = arr.filter(i => { const s = normalizeStatus(i); return s === "rejected" || s === "inactive" || s === "closed"; }).length;
  return { approved, pending, rejected, total: arr.length };
};

const getStateFromMember = (member) => {
  if (!member) return "";
  const direct = member.state || member.State || member.state_name || member.stateName || member.address_state || member.city_state || "";
  if (direct) return direct;
  const personal = member.personal || member.personal_details || member.personal_info || {};
  if (personal.state || personal.State || personal.state_name) return personal.state || personal.State || personal.state_name || "";
  const emp = member.employee_details || {};
  if (emp.state || emp.State || emp.state_name) return emp.state || emp.State || emp.state_name || "";
  const stu = member.student_details || {};
  if (stu.state || stu.State || stu.state_name) return stu.state || stu.State || stu.state_name || "";
  const uni = member.university_details || {};
  if (uni.state || uni.State || uni.state_name) return uni.state || uni.State || uni.state_name || "";
  const contact = member.contact_details || member.address || {};
  if (contact.state || contact.State || contact.state_name) return contact.state || contact.State || contact.state_name || "";
  return "";
};

// NEW: extract state for the dashboard data shape -> { member: {...state}, details: {...} }
const getMemberState = (item) => {
  if (!item) return "";
  // If item itself looks like {member: {...}, details: {...}}, use member.state
  if (item.member) {
    return getStateFromMember(item.member);
  }
  // otherwise fall back to old flat shape
  return getStateFromMember(item);
};

/* =========================================================
   STATE-WISE BREAKDOWN BUILDER
========================================================= */
const buildStateBreakdown = (employees, students, tpos) => {
  const breakdown = {};
  ALL_STATES.forEach(s => {
    breakdown[s.toLowerCase()] = { employees: 0, students: 0, tpos: 0, total: 0 };
  });

  const addMember = (item, key) => {
    const raw = (getMemberState(item) || "").trim();
    if (!raw) return;
    const lowerRaw = raw.toLowerCase();
    let matchKey = null;

    if (breakdown.hasOwnProperty(lowerRaw)) {
      matchKey = lowerRaw;
    } else {
      const found = ALL_STATES.find(s =>
        s.toLowerCase().includes(lowerRaw) || lowerRaw.includes(s.toLowerCase())
      );
      if (found) matchKey = found.toLowerCase();
    }

    if (matchKey) {
      breakdown[matchKey][key]++;
      breakdown[matchKey].total++;
    }
  };

  employees.forEach(m => addMember(m, "employees"));
  students.forEach(m => addMember(m, "students"));
  tpos.forEach(m => addMember(m, "tpos"));

  return breakdown;
};

/* =========================================================
   CONSTANTS
========================================================= */
const ALL_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand",
  "Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal","Jammu and Kashmir",
  "Delhi","Puducherry","Chandigarh","Andaman and Nicobar",
  "Dadra and Nagar Haveli","Daman and Diu","Lakshadweep",
];

// Full vivid colors — used for ALL states always (no light/muted version)
const STATE_COLORS = {
  "Andhra Pradesh":         "#E53935",
  "Arunachal Pradesh":      "#8E24AA",
  "Assam":                  "#3949AB",
  "Bihar":                  "#00897B",
  "Chhattisgarh":           "#F4511E",
  "Goa":                    "#039BE5",
  "Gujarat":                "#43A047",
  "Haryana":                "#FB8C00",
  "Himachal Pradesh":       "#6D4C41",
  "Jharkhand":              "#E91E63",
  "Karnataka":              "#1E88E5",
  "Kerala":                 "#00ACC1",
  "Madhya Pradesh":         "#7CB342",
  "Maharashtra":            "#9C27B0",
  "Manipur":                "#F06292",
  "Meghalaya":              "#26C6DA",
  "Mizoram":                "#F9A825",
  "Nagaland":               "#EF5350",
  "Odisha":                 "#42A5F5",
  "Punjab":                 "#66BB6A",
  "Rajasthan":              "#FF7043",
  "Sikkim":                 "#AB47BC",
  "Tamil Nadu":             "#FF6F00",
  "Telangana":              "#2196F3",
  "Tripura":                "#26A69A",
  "Uttar Pradesh":          "#D32F2F",
  "Uttarakhand":            "#0288D1",
  "West Bengal":            "#558B2F",
  "Jammu and Kashmir":      "#5C6BC0",
  "Delhi":                  "#FF5722",
  "Puducherry":             "#FFA726",
  "Chandigarh":             "#EC407A",
  "Andaman and Nicobar":    "#29B6F6",
  "Dadra and Nagar Haveli": "#9CCC65",
  "Daman and Diu":          "#80CBC4",
  "Lakshadweep":            "#FFAB40",
};

// A rotating fallback palette so unknown GeoJSON names still get a vivid color
const FALLBACK_COLORS = [
  "#E53935","#8E24AA","#3949AB","#00897B","#F4511E","#039BE5",
  "#43A047","#FB8C00","#E91E63","#1E88E5","#00ACC1","#7CB342",
  "#9C27B0","#F06292","#26C6DA","#F9A825","#EF5350","#42A5F5",
  "#66BB6A","#FF7043","#AB47BC","#FF6F00","#2196F3","#26A69A",
  "#D32F2F","#0288D1","#558B2F","#5C6BC0","#FF5722","#FFA726",
  "#EC407A","#29B6F6","#9CCC65","#80CBC4","#FFAB40","#6D4C41",
];

/* =========================================================
   COMPONENTS
========================================================= */
const StatCard = ({ icon, label, value, color }) => (
  <div style={{
    background:"#fff",borderRadius:"12px",padding:"16px 20px",
    display:"flex",alignItems:"center",gap:"14px",
    boxShadow:"0 2px 8px rgba(0,0,0,0.08)",flex:"1",minWidth:"130px",
  }}>
    <div style={{
      width:"46px",height:"46px",borderRadius:"10px",
      background:color+"22",color,display:"flex",
      alignItems:"center",justifyContent:"center",fontSize:"22px",flexShrink:0,
    }}>{icon}</div>
    <div>
      <div style={{fontSize:"12px",color:"#888",fontWeight:500}}>{label}</div>
      <div style={{fontSize:"22px",fontWeight:700,color:"#1a1a2e"}}>{value}</div>
    </div>
  </div>
);

const SectionTitle = ({ title }) => (
  <div style={{
    fontWeight:700,fontSize:"15px",color:"#1a1a2e",marginBottom:"14px",
    borderLeft:"4px solid #0d6efd",paddingLeft:"10px",
  }}>{title}</div>
);

const SkeletonCard = () => (
  <div style={{
    background:"#fff",borderRadius:"12px",padding:"16px 20px",
    display:"flex",alignItems:"center",gap:"14px",
    boxShadow:"0 2px 8px rgba(0,0,0,0.08)",flex:"1",minWidth:"130px",
  }}>
    <div style={{width:"46px",height:"46px",borderRadius:"10px",background:"#f0f0f0",animation:"pulse 1.5s infinite"}}/>
    <div>
      <div style={{width:"70px",height:"12px",background:"#f0f0f0",borderRadius:"4px",marginBottom:"8px",animation:"pulse 1.5s infinite"}}/>
      <div style={{width:"50px",height:"22px",background:"#f0f0f0",borderRadius:"4px",animation:"pulse 1.5s infinite"}}/>
    </div>
  </div>
);

/* =========================================================
   MAIN DASHBOARD
========================================================= */
const DashboardHome = () => {
  const [hoveredState, setHoveredState] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [errors, setErrors]             = useState([]);

  const [stats, setStats] = useState({
    employees:0, students:0, tpos:0, jobs:0,
    applications:0, boardMembers:0, events:0,
    trainings:0, jobFairs:0, blackProfiles:0,
    publications:0, contacts:0,
  });

  // STATE-WISE BREAKDOWN: { 'telangana': {employees, students, tpos, total}, ... }
  const [stateBreakdownMap, setStateBreakdownMap] = useState({});

  // Chart data: array of { state, Employees, Students, TPOs, total }
  const [stateBreakdownChart, setStateBreakdownChart] = useState(
    ALL_STATES.map(s => ({ state:s, Employees:0, Students:0, TPOs:0, total:0 }))
  );

  const [membershipStatusData, setMembershipStatusData] = useState([
    { name:"Approved", value:0, color:"#28a745" },
    { name:"Pending",  value:0, color:"#ffc107" },
    { name:"Rejected", value:0, color:"#dc3545" },
  ]);

  const [userDistData, setUserDistData] = useState([
    { name:"Students",  value:0, color:"#0d6efd" },
    { name:"Employees", value:0, color:"#6f42c1" },
    { name:"TPOs",      value:0, color:"#20c997" },
  ]);

  // STATE TABLE: includes employees/students/tpos breakdown
  const [stateData, setStateData] = useState(
    ALL_STATES.map(s => ({
      state:s, count:0, employees:0, students:0, tpos:0,
      color:STATE_COLORS[s]||"#90CAF9"
    }))
  );

  const [recruitment, setRecruitment] = useState({
    activeJobs:0, pendingJobs:0, inactiveJobs:0,
    totalApplications:0, shortlisted:0, pendingApps:0, rejectedApps:0,
  });

  const [permissions, setPermissions] = useState([
    { label:"Employees", icon:<FaUsers/>,             color:"#6f42c1", approved:0,pending:0,rejected:0,total:0 },
    { label:"Students",  icon:<FaUserGraduate/>,      color:"#0d6efd", approved:0,pending:0,rejected:0,total:0 },
    { label:"TPOs",      icon:<FaChalkboardTeacher/>, color:"#20c997", approved:0,pending:0,rejected:0,total:0 },
  ]);

  const [serviceApps, setServiceApps] = useState([
    { label:"Global Network",    icon:<FaNetworkWired/>, approved:0, pending:0, rejected:0 },
    { label:"Knowledge Sharing", icon:<FaUserGraduate/>, approved:0, pending:0, rejected:0 },
    { label:"Skills & Training", icon:<FaLaptop/>,       approved:0, pending:0, rejected:0 },
    { label:"Jobs & Job Fairs",  icon:<FaBriefcase/>,    approved:0, pending:0, rejected:0 },
    { label:"Events",            icon:<FaCalendarAlt/>,  approved:0, pending:0, rejected:0 },
  ]);

  const [bottomCards, setBottomCards] = useState({
    totalMembers:0, approvedMembers:0, pendingMembers:0,
    rejectedMembers:0, events:0, trainings:0,
  });

  /* =========================================================
     FETCH ALL
  ========================================================= */
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const errs = [];
      try {
        const [
          empRes, stuRes, repRes, jobRes,
          boardRes, eventRes, trainingRes, jobFairRes,
          blackProfilesRes, publicationsRes, contactsRes, registrationsRes,
        ] = await Promise.allSettled([
          ApiService.AdminAPI.getEmployees(),
          ApiService.AdminAPI.getStudents(),
          ApiService.AdminAPI.getRepresentatives(),
          ApiService.JobAPI.getAdminAllJobs(),
          ApiService.BoardMembersAPI.getAllBoardMembers(),
          ApiService.EventAPI.getAdminEvents(),
          ApiService.TrainingAPI.getAllTrainings(),
          ApiService.JobFairAPI.getAllJobFairs(),
          ApiService.BlackProfilesAPI.getAllBlackProfiles(),
          ApiService.TalentPublicationsAPI.getAllPublications(),
          ApiService.ContactAPI.getAllContacts(),
          ApiService.EventAPI.getAllRegistrations(),
        ]);

        const apiNames = [
          "employees","students","representatives","jobs","boardMembers",
          "events","trainings","jobFairs","blackProfiles","publications","contacts","registrations",
        ];
        [empRes,stuRes,repRes,jobRes,boardRes,eventRes,trainingRes,jobFairRes,
          blackProfilesRes,publicationsRes,contactsRes,registrationsRes,
        ].forEach((r,i) => {
          if (r.status === "rejected") {
            errs.push(apiNames[i]);
            console.warn(`API FAILED [${apiNames[i]}]:`, r.reason?.response?.status, r.reason?.message);
          } else {
            console.log(`API OK [${apiNames[i]}]:`, r.value?.data);
          }
        });

        const employees    = safeArr(empRes);
        const students     = safeArr(stuRes);
        const tpos         = safeArr(repRes);
        const jobs         = safeArr(jobRes);
        const boardMembers = safeArr(boardRes);
        const events       = safeArr(eventRes);
        const trainings    = safeArr(trainingRes);
        const jobFairs     = safeArr(jobFairRes);
        const blackProfiles  = safeArr(blackProfilesRes);
        const publications   = safeArr(publicationsRes);
        const contacts       = safeArr(contactsRes);

        console.log("SAMPLE EMPLOYEE:", employees[0]);
        console.log("SAMPLE STUDENT:", students[0]);
        console.log("SAMPLE TPO:", tpos[0]);

        /* ── For status counting, use .member sub-object if present ── */
        const getStatusItem = (item) => item?.member ? item.member : item;

        const empStatus      = countByStatus(employees.map(getStatusItem));
        const stuStatus      = countByStatus(students.map(getStatusItem));
        const tpoStatus      = countByStatus(tpos.map(getStatusItem));
        const trainingStatus = countByStatus(trainings);
        const jobFairStatus  = countByStatus(jobFairs);
        const eventStatus    = countByStatus(events);

        const allMembersForStatus = [
          ...employees.map(getStatusItem),
          ...students.map(getStatusItem),
          ...tpos.map(getStatusItem),
        ];
        const allStatus     = countByStatus(allMembersForStatus);
        const totalApproved = allStatus.approved;
        const totalPending  = allStatus.pending;
        const totalRejected = allStatus.rejected;

        const totalApplications = jobs.reduce((sum, job) =>
          sum + (job.application_count ?? job.applications_count ??
            job.total_applications ?? job.applicants_count ??
            job.applicants ?? job.applied_count ?? 0), 0);
        const totalShortlisted  = jobs.reduce((sum,j) => sum + (j.shortlisted_count ?? j.shortlisted ?? 0), 0);
        const totalPendingApps  = jobs.reduce((sum,j) => sum + (j.pending_count ?? j.pending_applications ?? j.pending ?? 0), 0);
        const totalRejectedApps = jobs.reduce((sum,j) => sum + (j.rejected_count ?? j.rejected ?? 0), 0);

        const activeJobs   = jobs.filter(j => { const s=normalizeStatus(j); return s==="active"||s==="approved"||s==="open"; }).length;
        const pendingJobs  = jobs.filter(j => normalizeStatus(j)==="pending").length;
        const inactiveJobs = jobs.filter(j => { const s=normalizeStatus(j); return s==="inactive"||s==="rejected"||s==="closed"; }).length;

        setStats({
          employees:employees.length, students:students.length, tpos:tpos.length,
          jobs:jobs.length, applications:totalApplications, boardMembers:boardMembers.length,
          events:events.length, trainings:trainings.length, jobFairs:jobFairs.length,
          blackProfiles:blackProfiles.length, publications:publications.length, contacts:contacts.length,
        });

        setUserDistData([
          { name:"Students",  value:students.length,  color:"#0d6efd" },
          { name:"Employees", value:employees.length, color:"#6f42c1" },
          { name:"TPOs",      value:tpos.length,      color:"#20c997" },
        ]);

        setMembershipStatusData([
          { name:"Approved", value:totalApproved, color:"#28a745" },
          { name:"Pending",  value:totalPending,  color:"#ffc107" },
          { name:"Rejected", value:totalRejected, color:"#dc3545" },
        ]);

        setPermissions([
          { label:"Employees", icon:<FaUsers/>,             color:"#6f42c1", ...empStatus },
          { label:"Students",  icon:<FaUserGraduate/>,      color:"#0d6efd", ...stuStatus },
          { label:"TPOs",      icon:<FaChalkboardTeacher/>, color:"#20c997", ...tpoStatus },
        ]);

        setRecruitment({
          activeJobs, pendingJobs, inactiveJobs, totalApplications,
          shortlisted:totalShortlisted, pendingApps:totalPendingApps, rejectedApps:totalRejectedApps,
        });

        /* ── STATE-WISE BREAKDOWN (Employees / Students / TPOs) ── */
        const breakdown = buildStateBreakdown(employees, students, tpos);
        setStateBreakdownMap(breakdown);

        Object.entries(breakdown).filter(([,v])=>v.total>0)
          .forEach(([k,v]) => console.log(`  STATE WITH MEMBERS: ${k} =`, v));

        // stateData for table (sorted by total desc) — ALL 29 STATES
        const stateRows = ALL_STATES.map(stateName => {
          const b = breakdown[stateName.toLowerCase()];
          return {
            state:  stateName,
            count:  b.total,
            employees: b.employees,
            students:  b.students,
            tpos:      b.tpos,
            color:  STATE_COLORS[stateName] || "#90CAF9",
          };
        }).sort((a,b) => b.count - a.count);
        setStateData(stateRows);

        // Chart data — all states, stacked Employees/Students/TPOs, sorted by total desc
        const chartRows = ALL_STATES.map(stateName => {
          const b = breakdown[stateName.toLowerCase()];
          return {
            state: stateName,
            Employees: b.employees,
            Students:  b.students,
            TPOs:      b.tpos,
            total:     b.total,
          };
        }).sort((a,b) => b.total - a.total);
        setStateBreakdownChart(chartRows);

        setServiceApps([
          { label:"Global Network",    icon:<FaNetworkWired/>, approved:totalApproved, pending:totalPending,  rejected:totalRejected },
          { label:"Knowledge Sharing", icon:<FaUserGraduate/>, approved:totalApproved, pending:totalPending,  rejected:totalRejected },
          { label:"Skills & Training", icon:<FaLaptop/>,       approved:trainingStatus.approved || trainings.length, pending:trainingStatus.pending, rejected:trainingStatus.rejected },
          { label:"Jobs & Job Fairs",  icon:<FaBriefcase/>,    approved:activeJobs + jobFairStatus.approved, pending:pendingJobs + jobFairStatus.pending, rejected:inactiveJobs + jobFairStatus.rejected },
          { label:"Events",            icon:<FaCalendarAlt/>,  approved:eventStatus.approved || events.length, pending:eventStatus.pending, rejected:eventStatus.rejected },
        ]);

        setBottomCards({
          totalMembers:employees.length + students.length + tpos.length, approvedMembers:totalApproved,
          pendingMembers:totalPending, rejectedMembers:totalRejected,
          events:events.length, trainings:trainings.length,
        });

      } catch (err) {
        console.error("FETCH ERROR:", err);
        errs.push("Unexpected error");
      } finally {
        setErrors(errs);
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  /* =========================================================
     MAP HELPERS
  ========================================================= */
  // Extract state name from GeoJSON properties — tries many field names
  const getGeoStateName = (props) =>
    props.NAME_1 || props.name || props.Name || props.NAME ||
    props.st_nm  || props.ST_NM || props.State || props.state ||
    props.statename || props.STATENAME || props.state_name ||
    props.GEO_ID || props.ID_1 || "";

  // Get vivid color for a GeoJSON state name — always returns a full color
  const getStateColor = (geoName, fallbackIndex) => {
    if (!geoName) return FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length];
    // Exact match
    if (STATE_COLORS[geoName]) return STATE_COLORS[geoName];
    // Case-insensitive
    const lower = geoName.toLowerCase();
    const found = Object.keys(STATE_COLORS).find(k => k.toLowerCase() === lower);
    if (found) return STATE_COLORS[found];
    // Partial match
    const partial = Object.keys(STATE_COLORS).find(k =>
      k.toLowerCase().includes(lower) || lower.includes(k.toLowerCase())
    );
    if (partial) return STATE_COLORS[partial];
    // Fallback: assign from rotating palette so it's never grey
    return FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length];
  };

  // Get full breakdown (employees/students/tpos/total) for a GeoJSON state name
  const getStateBreakdownForGeo = (geoName) => {
    if (!geoName) return { employees:0, students:0, tpos:0, total:0 };
    const lower = geoName.toLowerCase();
    if (stateBreakdownMap.hasOwnProperty(lower)) return stateBreakdownMap[lower];
    // Try fuzzy
    const key = Object.keys(stateBreakdownMap).find(k =>
      k.includes(lower) || lower.includes(k)
    );
    return key ? stateBreakdownMap[key] : { employees:0, students:0, tpos:0, total:0 };
  };

  // Get just the total member count for a GeoJSON state name
  const getMemberCount = (geoName) => getStateBreakdownForGeo(geoName).total;

  const userTotal   = Math.max(userDistData.reduce((s,i) => s+i.value, 0), 1);
  const memberTotal = membershipStatusData.reduce((s,i) => s+i.value, 0);

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div style={{background:"#f0f2f5",minHeight:"100vh",padding:"20px",fontFamily:"Segoe UI, sans-serif"}}>
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {errors.length > 0 && (
        <div style={{background:"#fff3cd",border:"1px solid #ffc107",borderRadius:"8px",
          padding:"10px 16px",marginBottom:"16px",fontSize:"13px",color:"#856404"}}>
          ⚠️ Could not load: {errors.join(", ")}. Showing available data.
        </div>
      )}

      {/* ROW 1 — PRIMARY STAT CARDS */}
      <div style={{display:"flex",gap:"12px",flexWrap:"wrap",marginBottom:"14px"}}>
        {loading ? Array(6).fill(0).map((_,i) => <SkeletonCard key={i}/>) : (
          <>
            <StatCard icon={<FaUsers/>}             label="Total Employees"  value={stats.employees.toLocaleString()}    color="#6f42c1"/>
            <StatCard icon={<FaUserGraduate/>}      label="Total Students"   value={stats.students.toLocaleString()}     color="#0d6efd"/>
            <StatCard icon={<FaChalkboardTeacher/>} label="Total TPOs"       value={stats.tpos.toLocaleString()}         color="#20c997"/>
            <StatCard icon={<FaBriefcase/>}         label="Total Jobs"       value={stats.jobs.toLocaleString()}         color="#fd7e14"/>
            <StatCard icon={<FaFileAlt/>}           label="Job Applications" value={stats.applications.toLocaleString()} color="#dc3545"/>
            <StatCard icon={<FaUserTie/>}           label="Board Members"    value={stats.boardMembers.toLocaleString()} color="#0dcaf0"/>
          </>
        )}
      </div>

      {/* ROW 1B — SECONDARY STAT CARDS */}
      <div style={{display:"flex",gap:"12px",flexWrap:"wrap",marginBottom:"20px"}}>
        {loading ? Array(6).fill(0).map((_,i) => <SkeletonCard key={i}/>) : (
          <>
            <StatCard icon={<FaCalendarAlt/>} label="Events"            value={stats.events.toLocaleString()}        color="#e91e63"/>
            <StatCard icon={<FaLaptop/>}       label="Training Programs" value={stats.trainings.toLocaleString()}     color="#673ab7"/>
            <StatCard icon={<FaHandshake/>}    label="Job Fairs"         value={stats.jobFairs.toLocaleString()}      color="#009688"/>
            <StatCard icon={<FaUserSlash/>}    label="Black Profiles"    value={stats.blackProfiles.toLocaleString()} color="#f44336"/>
            <StatCard icon={<FaNewspaper/>}    label="Publications"      value={stats.publications.toLocaleString()}  color="#ff9800"/>
            <StatCard icon={<FaBuilding/>}     label="Contacts"          value={stats.contacts.toLocaleString()}      color="#795548"/>
          </>
        )}
      </div>

      {/* ROW 2 — USER DISTRIBUTION | STATE-WISE BREAKDOWN CHART | MEMBERSHIP STATUS */}
      <div style={{display:"flex",gap:"14px",flexWrap:"wrap",marginBottom:"20px"}}>

        {/* USER DISTRIBUTION */}
        <div style={{background:"#fff",borderRadius:"12px",padding:"20px",flex:"1",minWidth:"240px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
          <SectionTitle title="User Distribution"/>
          {loading ? (
            <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:"140px"}}>
              <div style={{width:"100px",height:"100px",borderRadius:"50%",background:"#f0f0f0",animation:"pulse 1.5s infinite"}}/>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie
                    data={userDistData.every(d=>d.value===0)
                      ? [{name:"Students",value:1,color:"#0d6efd"},{name:"Employees",value:1,color:"#6f42c1"},{name:"TPOs",value:1,color:"#20c997"}]
                      : userDistData}
                    cx="50%" cy="50%" innerRadius={38} outerRadius={62} dataKey="value"
                  >
                    {(userDistData.every(d=>d.value===0)
                      ? [{color:"#0d6efd"},{color:"#6f42c1"},{color:"#20c997"}]
                      : userDistData
                    ).map((e,i) => <Cell key={i} fill={e.color}/>)}
                  </Pie>
                  <Tooltip formatter={(v,n) => [`${v} (${((v/userTotal)*100).toFixed(1)}%)`, n]}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                {userDistData.map((item,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"8px"}}>
                    <div style={{width:"12px",height:"12px",borderRadius:"3px",background:item.color,flexShrink:0}}/>
                    <span style={{fontSize:"13px",color:"#444"}}>{item.name}</span>
                    <span style={{fontSize:"13px",fontWeight:700,color:"#1a1a2e"}}>
                      {item.value}
                      <span style={{fontSize:"11px",color:"#888",marginLeft:"4px"}}>({((item.value/userTotal)*100).toFixed(1)}%)</span>
                    </span>
                  </div>
                ))}
                <div style={{fontSize:"11px",color:"#888",borderTop:"1px solid #f0f0f0",paddingTop:"4px"}}>
                  Total: {userDistData.reduce((s,i)=>s+i.value,0)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* STATE-WISE BREAKDOWN CHART (replaces Registration Trend) */}
        <div style={{background:"#fff",borderRadius:"12px",padding:"20px",flex:"2",minWidth:"300px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
          <SectionTitle title="State Wise Members - Employees / Students / TPOs (Top 10)"/>
          {loading ? (
            <div style={{height:"170px",background:"#f8f9fa",borderRadius:"8px",animation:"pulse 1.5s infinite"}}/>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stateBreakdownChart.slice(0, 10)} margin={{ bottom: 50, left: 0, right: 10, top: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="state" tick={{fontSize:10}} angle={-35} textAnchor="end" interval={0}/>
                <YAxis tick={{fontSize:11}} allowDecimals={false}/>
                <Tooltip/>
                <Legend wrapperStyle={{fontSize:"11px"}}/>
                <Bar dataKey="Employees" stackId="a" fill="#6f42c1"/>
                <Bar dataKey="Students"  stackId="a" fill="#0d6efd"/>
                <Bar dataKey="TPOs"      stackId="a" fill="#20c997"/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* MEMBERSHIP STATUS */}
        <div style={{background:"#fff",borderRadius:"12px",padding:"20px",flex:"1",minWidth:"220px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
          <SectionTitle title="Membership Status"/>
          {loading ? (
            <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:"130px"}}>
              <div style={{width:"80px",height:"80px",borderRadius:"50%",background:"#f0f0f0",animation:"pulse 1.5s infinite"}}/>
            </div>
          ) : (
            <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
              <ResponsiveContainer width={120} height={130}>
                <PieChart>
                  <Pie
                    data={memberTotal === 0
                      ? [{name:"Approved",value:1,color:"#28a745"},{name:"Pending",value:1,color:"#ffc107"},{name:"Rejected",value:1,color:"#dc3545"}]
                      : membershipStatusData}
                    cx="50%" cy="50%" innerRadius={33} outerRadius={55} dataKey="value"
                  >
                    {(memberTotal === 0
                      ? [{color:"#28a745"},{color:"#ffc107"},{color:"#dc3545"}]
                      : membershipStatusData
                    ).map((e,i) => <Cell key={i} fill={e.color}/>)}
                  </Pie>
                  <Tooltip/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{display:"flex",flexDirection:"column",gap:"7px"}}>
                {membershipStatusData.map((item,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"8px"}}>
                    <div style={{width:"10px",height:"10px",borderRadius:"50%",background:item.color,flexShrink:0}}/>
                    <span style={{fontSize:"12px",color:"#444"}}>{item.name}</span>
                    <span style={{
                      fontSize:"12px",fontWeight:700,color:"#fff",background:item.color,
                      borderRadius:"10px",padding:"1px 7px",minWidth:"22px",textAlign:"center",
                    }}>{item.value}</span>
                  </div>
                ))}
                <div style={{fontSize:"11px",color:"#888",borderTop:"1px solid #f0f0f0",paddingTop:"4px",marginTop:"2px"}}>
                  Total members: {memberTotal}
                </div>
                <div style={{fontSize:"12px",color:"#28a745",fontWeight:700}}>
                  ✓ Active: {membershipStatusData[0]?.value || 0}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 3 — STATE WISE OVERVIEW */}
      <div style={{background:"#fff",borderRadius:"12px",padding:"20px",marginBottom:"20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <SectionTitle title="State Wise Member Overview (29 States) - Employees / Students / TPOs"/>
        <div style={{display:"flex",gap:"20px",flexWrap:"wrap",minHeight:"420px"}}>

          {/* STATE TABLE */}
          <div style={{flex:"1",minWidth:"320px",height:"420px",overflowY:"auto",borderRadius:"8px",border:"1px solid #f0f0f0"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead style={{position:"sticky",top:0,zIndex:1,background:"#f8f9fa"}}>
                <tr>
                  <th style={{padding:"10px 14px",textAlign:"left",fontSize:"13px",color:"#555",fontWeight:600}}>State</th>
                  <th style={{padding:"10px 8px",textAlign:"right",fontSize:"12px",color:"#6f42c1",fontWeight:600}}>Emp</th>
                  <th style={{padding:"10px 8px",textAlign:"right",fontSize:"12px",color:"#0d6efd",fontWeight:600}}>Stu</th>
                  <th style={{padding:"10px 8px",textAlign:"right",fontSize:"12px",color:"#20c997",fontWeight:600}}>TPO</th>
                  <th style={{padding:"10px 14px",textAlign:"right",fontSize:"13px",color:"#555",fontWeight:600}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array(12).fill(0).map((_,i) => (
                    <tr key={i}>
                      <td style={{padding:"9px 14px"}} colSpan={5}>
                        <div style={{width:"100%",height:"12px",background:"#f0f0f0",borderRadius:"4px",animation:"pulse 1.5s infinite"}}/>
                      </td>
                    </tr>
                  ))
                ) : (
                  stateData.map((row,i) => (
                    <tr key={i}
                      style={{
                        borderBottom:"1px solid #f0f0f0",
                        background: hoveredState?.toLowerCase() === row.state.toLowerCase()
                          ? row.color + "22" : "transparent",
                        cursor:"pointer",transition:"background 0.15s",
                      }}
                      onMouseEnter={() => setHoveredState(row.state)}
                      onMouseLeave={() => setHoveredState(null)}
                    >
                      <td style={{padding:"8px 14px",fontSize:"13px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                          <div style={{
                            width:"12px",height:"12px",borderRadius:"3px",
                            background:row.color, flexShrink:0,
                          }}/>
                          <span style={{color:"#333"}}>{row.state}</span>
                        </div>
                      </td>
                      <td style={{padding:"8px 8px",fontSize:"12px",textAlign:"right",fontWeight:600,color:"#6f42c1"}}>
                        {row.employees > 0 ? row.employees : <span style={{color:"#ccc"}}>0</span>}
                      </td>
                      <td style={{padding:"8px 8px",fontSize:"12px",textAlign:"right",fontWeight:600,color:"#0d6efd"}}>
                        {row.students > 0 ? row.students : <span style={{color:"#ccc"}}>0</span>}
                      </td>
                      <td style={{padding:"8px 8px",fontSize:"12px",textAlign:"right",fontWeight:600,color:"#20c997"}}>
                        {row.tpos > 0 ? row.tpos : <span style={{color:"#ccc"}}>0</span>}
                      </td>
                      <td style={{padding:"8px 14px",fontSize:"13px",textAlign:"right",fontWeight:600}}>
                        {row.count > 0 ? (
                          <span style={{
                            background:row.color,color:"#fff",
                            padding:"2px 10px",borderRadius:"10px",fontSize:"12px",
                          }}>
                            {row.count.toLocaleString()}
                          </span>
                        ) : (
                          <span style={{color:"#bbb",fontSize:"12px"}}>0</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* INDIA MAP */}
          <div style={{flex:"2",minWidth:"300px",height:"420px",position:"relative"}}>
            {loading ? (
              <div style={{width:"100%",height:"390px",background:"#f0f0f0",borderRadius:"8px",
                animation:"pulse 1.5s infinite",display:"flex",alignItems:"center",
                justifyContent:"center",color:"#aaa",fontSize:"14px"}}>
                Loading map...
              </div>
            ) : (
              <>
                <ComposableMap
                  projection="geoMercator"
                  projectionConfig={{ scale:1000, center:[82,22] }}
                  style={{width:"100%",height:"390px"}}
                >
                  <Geographies geography={indiaGeoJson}>
                    {({ geographies }) => {
                      // Log GeoJSON keys once for debugging
                      if (geographies.length > 0) {
                        console.log("GEO PROPERTY KEYS:", Object.keys(geographies[0].properties));
                        console.log("SAMPLE GEO PROPS:", geographies[0].properties);
                        console.log("SAMPLE GEO NAME:", getGeoStateName(geographies[0].properties));
                      }
                      return geographies.map((geo, geoIndex) => {
                        const geoName  = getGeoStateName(geo.properties);
                        const count    = getMemberCount(geoName);
                        const isHovered = hoveredState &&
                          (hoveredState.toLowerCase() === geoName.toLowerCase() ||
                           geoName.toLowerCase().includes(hoveredState.toLowerCase()) ||
                           hoveredState.toLowerCase().includes(geoName.toLowerCase()));

                        // Always use full vivid color — never muted, never grey
                        const baseColor = getStateColor(geoName, geoIndex);

                        // States WITH members: full color
                        // States WITHOUT members: same color but slightly lighter (opacity via hex)
                        // Hovered: always brightest
                        let fill;
                        if (isHovered) {
                          fill = baseColor; // full vivid on hover
                        } else if (count > 0) {
                          fill = baseColor; // full vivid for states with members
                        } else {
                          // Slightly transparent but still clearly visible
                          fill = baseColor + "CC"; // ~80% — still clearly colored
                        }

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={fill}
                            stroke="#ffffff"
                            strokeWidth={1}
                            onMouseEnter={() => setHoveredState(geoName)}
                            onMouseLeave={() => setHoveredState(null)}
                            style={{
                              default:{ outline:"none", cursor:"pointer" },
                              hover:  { outline:"none", cursor:"pointer" },
                              pressed:{ outline:"none" },
                            }}
                          />
                        );
                      });
                    }}
                  </Geographies>
                </ComposableMap>

                {/* LEGEND */}
                <div style={{
                  position:"absolute",top:"8px",right:"8px",
                  background:"rgba(255,255,255,0.97)",borderRadius:"8px",
                  padding:"10px 14px",fontSize:"11px",
                  boxShadow:"0 1px 6px rgba(0,0,0,0.15)",
                }}>
                  <div style={{fontWeight:700,color:"#333",marginBottom:"8px",fontSize:"12px"}}>Legend</div>
                  <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"5px"}}>
                    <div style={{width:"14px",height:"14px",borderRadius:"3px",background:"#E53935"}}/>
                    <span style={{color:"#444"}}>Has members (vivid)</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                    <div style={{width:"14px",height:"14px",borderRadius:"3px",background:"#E53935CC"}}/>
                    <span style={{color:"#444"}}>No members (slight fade)</span>
                  </div>
                </div>

                {/* HOVER TOOLTIP — shows Employees / Students / TPO / Total breakdown */}
                {hoveredState && (() => {
                  const b = getStateBreakdownForGeo(hoveredState);
                  return (
                    <div style={{
                      textAlign:"left",fontSize:"12px",fontWeight:600,color:"#1a1a2e",
                      background:"rgba(255,255,255,0.98)",borderRadius:"8px",padding:"10px 16px",
                      display:"inline-block",position:"absolute",bottom:"6px",
                      left:"50%",transform:"translateX(-50%)",whiteSpace:"nowrap",
                      boxShadow:"0 2px 10px rgba(0,0,0,0.15)",border:"1px solid #e0e0e0",
                      minWidth:"200px",
                    }}>
                      <div style={{display:"flex",alignItems:"center",gap:"7px",marginBottom:"6px",fontSize:"13px",fontWeight:700}}>
                        <span style={{
                          display:"inline-block",width:"10px",height:"10px",borderRadius:"2px",
                          background:getStateColor(hoveredState, 0),
                        }}/>
                        {hoveredState}
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",gap:"12px",fontSize:"11px",color:"#6f42c1"}}>
                        <span>Employees</span><span>{b.employees}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",gap:"12px",fontSize:"11px",color:"#0d6efd"}}>
                        <span>Students</span><span>{b.students}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",gap:"12px",fontSize:"11px",color:"#20c997"}}>
                        <span>TPOs</span><span>{b.tpos}</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",gap:"12px",fontSize:"12px",color:"#1a1a2e",fontWeight:700,borderTop:"1px solid #f0f0f0",marginTop:"4px",paddingTop:"4px"}}>
                        <span>Total</span><span>{b.total}</span>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ROW 4 — RECRUITMENT OVERVIEW */}
      <div style={{background:"#fff",borderRadius:"12px",padding:"20px",marginBottom:"20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <SectionTitle title="Recruitment Overview"/>
        <div style={{display:"flex",gap:"12px",flexWrap:"wrap"}}>
          {[
            { label:"Active Jobs",        value:recruitment.activeJobs,        icon:<FaBriefcase/>,    color:"#28a745" },
            { label:"Pending Jobs",       value:recruitment.pendingJobs,       icon:<FaBriefcase/>,    color:"#ffc107" },
            { label:"Inactive Jobs",      value:recruitment.inactiveJobs,      icon:<FaBriefcase/>,    color:"#6c757d" },
            { label:"Total Applications", value:recruitment.totalApplications, icon:<FaFileAlt/>,      color:"#0d6efd" },
            { label:"Shortlisted",        value:recruitment.shortlisted,       icon:<FaUserGraduate/>, color:"#20c997" },
            { label:"Pending Apps",       value:recruitment.pendingApps,       icon:<FaFileAlt/>,      color:"#fd7e14" },
            { label:"Rejected Apps",      value:recruitment.rejectedApps,      icon:<FaFileAlt/>,      color:"#dc3545" },
          ].map((item,i) => (
            <div key={i} style={{
              flex:"1",minWidth:"100px",background:"#f8f9fa",borderRadius:"10px",
              padding:"16px",textAlign:"center",border:`2px solid ${item.color}33`,
            }}>
              {loading ? (
                <>
                  <div style={{width:"28px",height:"28px",background:"#e9ecef",borderRadius:"50%",margin:"0 auto 8px",animation:"pulse 1.5s infinite"}}/>
                  <div style={{width:"40px",height:"22px",background:"#e9ecef",borderRadius:"4px",margin:"0 auto",animation:"pulse 1.5s infinite"}}/>
                </>
              ) : (
                <>
                  <div style={{fontSize:"22px",color:item.color,marginBottom:"6px"}}>{item.icon}</div>
                  <div style={{fontSize:"22px",fontWeight:700,color:"#1a1a2e"}}>{item.value}</div>
                  <div style={{fontSize:"11px",color:"#666",marginTop:"4px"}}>{item.label}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ROW 5 — PERMISSION SUMMARY + SERVICES OVERVIEW */}
      <div style={{display:"flex",gap:"14px",flexWrap:"wrap",marginBottom:"20px"}}>

        {/* PERMISSION SUMMARY */}
        <div style={{background:"#fff",borderRadius:"12px",padding:"20px",flex:"1",minWidth:"280px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
          <SectionTitle title="Membership Permission Summary"/>
          <div style={{display:"flex",gap:"14px",flexWrap:"wrap"}}>
            {permissions.map((role,i) => (
              <div key={i} style={{flex:"1",minWidth:"90px",background:"#f8f9fa",borderRadius:"10px",padding:"14px",textAlign:"center"}}>
                <div style={{fontSize:"22px",color:role.color,marginBottom:"6px"}}>{role.icon}</div>
                <div style={{fontSize:"12px",fontWeight:600,color:"#333",marginBottom:"4px"}}>{role.label}</div>
                <div style={{fontSize:"20px",fontWeight:700,color:role.color,marginBottom:"10px"}}>
                  {loading ? "—" : role.total}
                </div>
                {[
                  {key:"approved",label:"Approved",color:"#28a745"},
                  {key:"pending", label:"Pending", color:"#ffc107"},
                  {key:"rejected",label:"Rejected",color:"#dc3545"},
                ].map(s => (
                  <div key={s.key} style={{display:"flex",justifyContent:"space-between",fontSize:"12px",marginBottom:"4px"}}>
                    <span style={{color:s.color,fontWeight:500}}>{s.label}</span>
                    <span style={{fontWeight:700,color:s.color}}>{loading ? "—" : role[s.key]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* SERVICES OVERVIEW */}
        <div style={{background:"#fff",borderRadius:"12px",padding:"20px",flex:"1",minWidth:"280px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
          <SectionTitle title="Services Overview"/>
          <div style={{display:"flex",flexDirection:"column",gap:"14px"}}>
            {serviceApps.map((app,i) => {
              const total = app.approved + app.pending + app.rejected || 1;
              const approvedPct = (app.approved / total) * 100;
              const pendingPct  = (app.pending  / total) * 100;
              return (
                <div key={i} style={{display:"flex",alignItems:"center",gap:"12px"}}>
                  <div style={{
                    width:"36px",height:"36px",borderRadius:"8px",background:"#e8f0fe",
                    color:"#0d6efd",display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:"16px",flexShrink:0,
                  }}>
                    {app.icon}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:"4px",flexWrap:"wrap",gap:"4px"}}>
                      <span style={{fontSize:"12px",fontWeight:600,color:"#333"}}>{app.label}</span>
                      <div style={{display:"flex",gap:"8px"}}>
                        <span style={{fontSize:"11px",color:"#28a745",fontWeight:700}}>{loading?"—":app.approved} ✓</span>
                        <span style={{fontSize:"11px",color:"#ffc107",fontWeight:700}}>{loading?"—":app.pending} ⏳</span>
                        <span style={{fontSize:"11px",color:"#dc3545",fontWeight:700}}>{loading?"—":app.rejected} ✕</span>
                      </div>
                    </div>
                    <div style={{height:"6px",background:"#e9ecef",borderRadius:"4px",overflow:"hidden",display:"flex"}}>
                      {loading ? (
                        <div style={{height:"100%",width:"50%",background:"#e0e0e0",animation:"pulse 1.5s infinite"}}/>
                      ) : (
                        <>
                          <div style={{height:"100%",width:`${approvedPct}%`,background:"#28a745",transition:"width 0.8s"}}/>
                          <div style={{height:"100%",width:`${pendingPct}%`, background:"#ffc107",transition:"width 0.8s"}}/>
                          <div style={{height:"100%",flex:1,background:"#f5c6cb"}}/>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ROW 6 — QUICK ACTIONS */}
      <div style={{background:"#fff",borderRadius:"12px",padding:"20px",marginBottom:"20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <SectionTitle title="Quick Actions"/>
        <div style={{display:"flex",gap:"12px",flexWrap:"wrap"}}>
          {[
            {label:"Add Job",               icon:<FaPlusCircle/>, color:"#0d6efd"},
            {label:"Add Board Member",       icon:<FaUserPlus/>,  color:"#6f42c1"},
            {label:"Add Benefits",           icon:<FaGift/>,      color:"#20c997"},
            {label:"Add Black Profile",      icon:<FaUserSlash/>, color:"#dc3545"},
            {label:"Add Talent Publication", icon:<FaNewspaper/>, color:"#fd7e14"},
          ].map((action,i) => (
            <button key={i} style={{
              flex:"1",minWidth:"120px",background:action.color+"11",
              border:`1.5px solid ${action.color}44`,borderRadius:"10px",
              padding:"18px 10px",cursor:"pointer",textAlign:"center",transition:"all 0.2s",
            }}
              onMouseEnter={e=>{
                e.currentTarget.style.background=action.color+"22";
                e.currentTarget.style.transform="translateY(-3px)";
                e.currentTarget.style.boxShadow=`0 6px 16px ${action.color}33`;
              }}
              onMouseLeave={e=>{
                e.currentTarget.style.background=action.color+"11";
                e.currentTarget.style.transform="translateY(0)";
                e.currentTarget.style.boxShadow="none";
              }}
            >
              <div style={{fontSize:"26px",color:action.color,marginBottom:"8px"}}>{action.icon}</div>
              <div style={{fontSize:"12px",fontWeight:600,color:"#333"}}>{action.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ROW 7 — BOTTOM SUMMARY CARDS */}
      <div style={{display:"flex",gap:"12px",flexWrap:"wrap"}}>
        {[
          {label:"Total Members",          value:loading?"—":bottomCards.totalMembers.toLocaleString(),    sub:"Employees + Students + TPOs", bg:"#1a1a2e"},
          {label:"Active (Approved)",       value:loading?"—":bottomCards.approvedMembers.toLocaleString(), sub:"Approved membership candidates",bg:"#28a745"},
          {label:"Pending Members",         value:loading?"—":bottomCards.pendingMembers.toLocaleString(),  sub:"Awaiting approval",            bg:"#e6a817"},
          {label:"Rejected Members",        value:loading?"—":bottomCards.rejectedMembers.toLocaleString(), sub:"Rejected candidates",          bg:"#dc3545"},
          {label:"Total Events",            value:loading?"—":bottomCards.events.toLocaleString(),          sub:"All events created",           bg:"#e91e63"},
          {label:"Training Programs",       value:loading?"—":bottomCards.trainings.toLocaleString(),       sub:"Active training programs",     bg:"#673ab7"},
        ].map((card,i) => (
          <div key={i} style={{
            flex:"1",minWidth:"140px",background:card.bg,borderRadius:"12px",
            padding:"20px",color:"#fff",boxShadow:"0 2px 8px rgba(0,0,0,0.15)",
          }}>
            <div style={{fontSize:"12px",opacity:0.8,marginBottom:"6px"}}>{card.label}</div>
            <div style={{fontSize:"28px",fontWeight:700,marginBottom:"4px"}}>{card.value}</div>
            <div style={{fontSize:"11px",opacity:0.75}}>{card.sub}</div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default DashboardHome;